import { hashWords } from "daedalus-core";
import { isPlainRecord } from "../rules/checks.ts";
import { ENABLED_SETS, MAX_LOOK_KEY_CHARS, isBareKey, isBarePiece, type LookPiece, type LookSlot } from "../rules/look-records.ts";
import { lookByKey, type Looks } from "../rules/looks.ts";

/**
 * A character's look, as rules (ADR-094 decisions 2, 4 and 5): what a character is (its appearance: a skin, a hair
 * and clothes, saved with it), what it has on (worn: an outfit, a hat and the two hands, from equipment), the one
 * function that composes them, the check a server runs on every appearance a page sends, and the look a character
 * with none is given. Pure: no clock, no randomness beyond the shared seeded hash, and nothing here reads art.
 */

/**
 * What a character is: three piece keys, chosen in the creator and saved on the character.
 */
export interface Appearance {
  readonly skin: string;
  readonly hair: string;
  readonly clothes: string;
}

/**
 * What a character has on: piece keys from its equipment, null for nothing in a slot.
 */
export interface Worn {
  readonly outfit: string | null;
  readonly hat: string | null;
  readonly mainHand: string | null;
  readonly offHand: string | null;
}

/**
 * A composed look: the piece key drawn in each slot, null for nothing. A character always has a skin, a hair key and
 * an outfit; the hair key may be the bare piece's (`NO_HAIR_KEY`), which is a piece of the table and draws nothing.
 */
export interface WornLook {
  readonly skin: string;
  readonly hair: string;
  readonly outfit: string;
  readonly hat: string | null;
  readonly mainHand: string | null;
  readonly offHand: string | null;
}

/**
 * What every character has on until equipment exists (ADR-094 decision 2): no outfit over its clothes, no hat, the
 * starter sword and the starter shield.
 */
export const STARTER_WORN: Worn = Object.freeze({ outfit: null, hat: null, mainHand: "sw01-1", offHand: "sh01-1" });

/**
 * The floor: the outfit a character is drawn in when nothing covers its body, the packs' boxers in their one colour.
 * No player chooses it and no item is it; it is what "no outfit" looks like, so no character is ever drawn unclothed.
 */
export const FLOOR_OUTFIT = "boxr-1";

/**
 * An appearance's fields, in the order they are checked, and the slot each one's piece is worn in.
 */
export const APPEARANCE_FIELDS = ["skin", "hair", "clothes"] as const;
export type AppearanceField = (typeof APPEARANCE_FIELDS)[number];

const FIELD_SLOTS: Readonly<Record<AppearanceField, LookSlot>> = { skin: "skin", hair: "hair", clothes: "outfit" };

/**
 * Why an appearance is refused, a closed list: `shape` (not a plain record of exactly the three fields, or a field
 * that is not a string of a key's length), `unknown` (a key the table lacks), `slot` (a piece of another slot, as a
 * hat or a weapon is), `creator` (a piece no player may choose) and `sets` (a piece that does not cover an enabled
 * combat set).
 */
export const APPEARANCE_REFUSALS = ["shape", "unknown", "slot", "creator", "sets"] as const;
export type AppearanceRefusal = (typeof APPEARANCE_REFUSALS)[number];

/**
 * What the check says of an appearance: the appearance, built from its checked fields, or the first field that is
 * wrong and why. `look` names the appearance as a whole.
 */
export type AppearanceVerdict = { readonly ok: true; readonly appearance: Appearance } | { readonly ok: false; readonly field: AppearanceField | "look"; readonly reason: AppearanceRefusal };

/**
 * The seed of the hash that picks a default look, and the most UTF-16 units of a seed text it reads.
 */
const LOOK_SEED = 0x6c6f6f6b;
const MAX_SEED_UNITS = 256;
const MAX_U32 = 0xffffffff;

/**
 * A key as an outfit to draw: the key, or null for nothing worn and for a bare key, which covers nothing.
 *
 * @param key A piece key, or null.
 * @returns The key, or null.
 */
function covering(key: string | null): string | null {
  return key === null || isBareKey(key) ? null : key;
}

/**
 * Composes what a character is with what it has on (ADR-094 decision 2): the skin and the hair from the appearance,
 * the hair as its key even when it is the hair that is none; the worn outfit, or the appearance's clothes when none
 * is worn, or the floor (`FLOOR_OUTFIT`) when neither covers the body, so a character is never undressed; the hat and
 * the hands from what is worn alone.
 *
 * @param appearance What the character is.
 * @param worn What it has on.
 * @returns The piece key drawn in each slot.
 * @example
 * lookOf({ skin: "humn-3", hair: "bob1-7", clothes: "fstr-2" }, STARTER_WORN);
 * // { skin: "humn-3", hair: "bob1-7", outfit: "fstr-2", hat: null, mainHand: "sw01-1", offHand: "sh01-1" }
 * lookOf({ skin: "humn-3", hair: "none-1", clothes: "none-1" }, STARTER_WORN);
 * // { skin: "humn-3", hair: "none-1", outfit: "boxr-1", hat: null, mainHand: "sw01-1", offHand: "sh01-1" }
 */
export function lookOf(appearance: Appearance, worn: Worn): WornLook {
  return { skin: appearance.skin, hair: appearance.hair, outfit: covering(worn.outfit) ?? covering(appearance.clothes) ?? FLOOR_OUTFIT, hat: worn.hat, mainHand: worn.mainHand, offHand: worn.offHand };
}

/**
 * Whether a piece covers every one of some combat sets.
 *
 * @param piece The piece.
 * @param sets The sets.
 * @returns True when it has every animation of each.
 */
function covers(piece: LookPiece, sets: readonly string[]): boolean {
  const held: readonly string[] = piece.sets;
  return sets.every((set) => held.includes(set));
}

/**
 * Checks an appearance a page sent against the looks table (ADR-094 decision 4). Never throws and never runs a
 * getter: anything but a plain record of exactly `skin`, `hair` and `clothes`, each a string, is `shape`; then each
 * field in turn names a piece the table holds (`unknown`), of the field's slot (`slot`, which refuses a hat or a
 * weapon), that a player may choose (`creator`) and that covers every enabled combat set (`sets`).
 *
 * @param looks The looks.
 * @param enabledSets The combat sets whose weapons the game ships, `ENABLED_SETS` in the game.
 * @param appearance What the page sent, untrusted.
 * @returns The appearance, built from the checked fields, or the first field refused and why.
 * @example
 * validateAppearance(rules.looks, ENABLED_SETS, { skin: "humn-1", hair: "pnty-1", clothes: "fstr-1" });
 * // { ok: false, field: "hair", reason: "slot" }
 */
export function validateAppearance(looks: Looks, enabledSets: readonly string[], appearance: unknown): AppearanceVerdict {
  if (!isPlainRecord(appearance) || Object.getOwnPropertySymbols(appearance).length > 0 || Object.keys(appearance).length !== APPEARANCE_FIELDS.length) {
    return { ok: false, field: "look", reason: "shape" };
  }
  const keys: string[] = [];
  for (const field of APPEARANCE_FIELDS) {
    const descriptor = Object.getOwnPropertyDescriptor(appearance, field);
    if (descriptor === undefined) {
      return { ok: false, field: "look", reason: "shape" };
    }
    const value: unknown = descriptor.value;
    if (descriptor.get !== undefined || descriptor.set !== undefined || typeof value !== "string" || value.length > MAX_LOOK_KEY_CHARS) {
      return { ok: false, field, reason: "shape" };
    }
    keys.push(value);
  }
  for (const [index, field] of APPEARANCE_FIELDS.entries()) {
    const piece = lookByKey(looks, keys[index] as string);
    if (piece === undefined) {
      return { ok: false, field, reason: "unknown" };
    }
    if (piece.slot !== FIELD_SLOTS[field]) {
      return { ok: false, field, reason: "slot" };
    }
    if (!piece.creator) {
      return { ok: false, field, reason: "creator" };
    }
    if (!covers(piece, enabledSets)) {
      return { ok: false, field, reason: "sets" };
    }
  }
  return { ok: true, appearance: { skin: keys[0] as string, hair: keys[1] as string, clothes: keys[2] as string } };
}

/**
 * The shared seeded hash of a seed text: its length, then its first `MAX_SEED_UNITS` UTF-16 units, a word each, so
 * any string has a digest, a lone surrogate included.
 *
 * @param seed The text.
 * @returns The unsigned 32-bit digest, the same on every engine.
 */
function seedDigest(seed: string): number {
  const units = Math.min(seed.length, MAX_SEED_UNITS);
  const words = new Uint32Array(1 + units);
  words[0] = Math.min(seed.length, MAX_U32);
  for (let index = 0; index < units; index += 1) {
    words[1 + index] = seed.charCodeAt(index);
  }
  return hashWords(LOOK_SEED, words, words.length);
}

/**
 * The look a character with no saved look is given (ADR-094 decision 5): a skin, a hair and clothes picked among the
 * pieces with art that a player may choose and that cover every enabled set, by the shared seeded hash over a text.
 * A bare piece is never in the pick: nobody is given no hair without choosing it, and since it was never in a slot's
 * pick its being in the table moves no default. The same seed gives
 * the same look on every engine and every run only while a slot's offered pieces stay exactly as they are: any change
 * to them, an offered piece appended or one withdrawn, or another list of enabled sets, moves the looks of most seeds
 * in that slot (the pick is a remainder by their count). So the gateway must store a character's look the first time
 * it derives it, and only a dev join's, which is never stored, is derived again.
 *
 * @param looks The looks, as the validator admits them: a skin, a hair and an outfit a player may choose.
 * @param seed The character's id, or a dev join's name.
 * @param enabledSets The combat sets whose weapons the game ships; `ENABLED_SETS` unless given. The same sets handed
 *   to `validateAppearance` accept the result.
 * @returns The appearance, which `validateAppearance` accepts under the same sets.
 * @throws Error when the table offers no piece for a slot under those sets, which no validated table does under the
 *   game's.
 * @example
 * defaultAppearance(loadRules().looks, "0199c0de-7a11-7000-8000-000000000001");
 * // one skin, one hair and one outfit, the same wherever it is asked
 */
export function defaultAppearance(looks: Looks, seed: string, enabledSets: readonly string[] = ENABLED_SETS): Appearance {
  const digest = seedDigest(seed);
  const words = new Uint32Array(1);
  const keys: string[] = [];
  for (const [index, field] of APPEARANCE_FIELDS.entries()) {
    const slot = FIELD_SLOTS[field];
    const offered = looks.pieces.filter((piece) => piece.slot === slot && piece.creator && !isBarePiece(piece) && covers(piece, enabledSets));
    if (offered.length === 0) {
      throw new Error(`the looks table offers no ${slot} piece, so no default look can be picked`);
    }
    words[0] = index;
    keys.push((offered[hashWords(digest, words, 1) % offered.length] as LookPiece).key);
  }
  return { skin: keys[0] as string, hair: keys[1] as string, clothes: keys[2] as string };
}
